/**
 * FieldMappingRow Component
 * Red za mapiranje jednog polja
 */

import { useState } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Box,
  InputLabel,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Transform as TransformIcon,
} from '@mui/icons-material';
import { FlatField, FieldTransformOptions, applyValueTransform } from '../../../lib/xmlParser';
import { FieldDefinition } from '../types';
import {
  isMultiValueField,
  isKeyValueField,
} from '../utils/fieldConfig';
import {
  getNestedValue,
  extractTextValue,
  getArrayObjectKeys,
} from '../utils/helpers';

interface FieldMappingRowProps {
  field: FieldDefinition;
  mapping: string;
  fieldOptions?: FieldTransformOptions;
  availableFields: FlatField[];
  sampleRecords: any[];
  sampleIndex: number;
  recordElement: string;
  isCustom?: boolean;
  onMappingChange: (dbField: string, xmlPath: string) => void;
  onFieldOptionChange: (dbField: string, option: Partial<FieldTransformOptions>) => void;
  onRemove?: () => void;
  onPreviewArray?: (fieldPath: string) => void;
}

export function FieldMappingRow({
  field,
  mapping,
  fieldOptions,
  availableFields,
  sampleRecords,
  sampleIndex,
  recordElement,
  isCustom,
  onMappingChange,
  onFieldOptionChange,
  onRemove,
  onPreviewArray,
}: FieldMappingRowProps) {
  const [showTransform, setShowTransform] = useState(false);
  const isMultiValue = isMultiValueField(field.key);
  const isKeyValue = isKeyValueField(field.key);
  const currentOption = fieldOptions || { mode: 'single' as const };
  const hasTransform = currentOption.transform && currentOption.transform.type !== 'none';
  const isPriceField = ['regular_price', 'nabavna_cijena', 'sale_price'].includes(field.key);
  const hasPriceAdjustment = !!(currentOption.priceMultiplier || currentOption.priceAddPercent);

  // Get available keys for keyValue configuration
  const availableKeys = mapping
    ? getArrayObjectKeys(sampleRecords, sampleIndex, mapping, recordElement)
    : [];

  // Get current value for preview
  const getPreviewValue = () => {
    if (!mapping || !sampleRecords?.[sampleIndex]) return null;

    const cleanPath = mapping.startsWith(recordElement + '/')
      ? mapping.substring(recordElement.length + 1)
      : mapping;

    return getNestedValue(sampleRecords[sampleIndex], cleanPath);
  };

  const previewValue = getPreviewValue();

  // Apply transform to preview value
  const getTransformedPreview = () => {
    if (!previewValue || Array.isArray(previewValue)) return previewValue;
    let result = previewValue;
    if (currentOption.transform && currentOption.transform.type !== 'none') {
      result = applyValueTransform(result, currentOption.transform);
    }
    // Apply price adjustment if configured
    if (isPriceField && (currentOption.priceMultiplier || currentOption.priceAddPercent)) {
      const num = parseFloat(String(result));
      if (!isNaN(num)) {
        let adjusted = num;
        if (currentOption.priceMultiplier) adjusted *= currentOption.priceMultiplier;
        if (currentOption.priceAddPercent) adjusted *= (1 + currentOption.priceAddPercent / 100);
        result = adjusted.toFixed(2);
      }
    }
    return result;
  };
  const transformedPreview = getTransformedPreview();
  const hasAnyTransform = hasTransform || hasPriceAdjustment;

  // Background color based on state
  const getBackgroundColor = () => {
    if (mapping) {
      if (isCustom) return 'rgba(25, 118, 210, 0.15)';
      return 'rgba(46, 125, 50, 0.15)';
    }
    if (field.required) return 'rgba(211, 47, 47, 0.15)';
    return 'inherit';
  };

  return (
    <TableRow sx={{ bgcolor: getBackgroundColor() }}>
      {/* Field Label */}
      <TableCell sx={{ width: isKeyValue && mapping ? 200 : 150 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" fontWeight={field.required ? 600 : 400}>
            {field.label}
            {field.required && <span style={{ color: '#f44336' }}> *</span>}
          </Typography>

          {/* Field type chip for custom fields */}
          {isCustom && (field as any).fieldType && (field as any).fieldType !== 'text' && (
            <Chip
              label={(field as any).fieldType === 'price' ? '€' : '#'}
              size="small"
              color={(field as any).fieldType === 'price' ? 'success' : 'info'}
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          )}

          {/* Remove button for custom fields */}
          {isCustom && onRemove && (
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Multi-value mode selector */}
        {isMultiValue && mapping && (
          <Box sx={{ mt: 0.5 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={currentOption.mode || 'single'}
                onChange={(e) => onFieldOptionChange(field.key, { mode: e.target.value as any })}
                sx={{ fontSize: '0.7rem', height: 24 }}
              >
                <MenuItem value="single">Prva vrijednost</MenuItem>
                <MenuItem value="array">Sve (array)</MenuItem>
                <MenuItem value="split">Razdvoji po ,</MenuItem>
                <MenuItem value="multiField">Kombiniraj polja</MenuItem>
              </Select>
            </FormControl>

            {/* MultiField configuration - additional fields input */}
            {currentOption.mode === 'multiField' && (
              <TextField
                size="small"
                fullWidth
                value={currentOption.multiFields?.join(', ') || ''}
                onChange={(e) => {
                  const fields = e.target.value
                    .split(',')
                    .map(f => f.trim())
                    .filter(f => f);
                  onFieldOptionChange(field.key, {
                    mode: 'multiField',
                    multiFields: fields,
                  });
                }}
                placeholder="Slika1, Slika2, Slika3..."
                helperText="Dodatna polja odvojena zarezom"
                sx={{
                  mt: 0.5,
                  '& .MuiInputBase-input': { fontSize: '0.7rem' },
                  '& .MuiFormHelperText-root': { fontSize: '0.6rem', mt: 0 },
                }}
              />
            )}
          </Box>
        )}

        {/* KeyValue configuration */}
        {isKeyValue && mapping && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {availableKeys.length > 0 ? (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.7rem' }}>Key polje</InputLabel>
                  <Select
                    value={currentOption.keyField || ''}
                    onChange={(e) => onFieldOptionChange(field.key, {
                      mode: 'keyValue',
                      keyField: e.target.value,
                    })}
                    label="Key polje"
                    sx={{ fontSize: '0.7rem', height: 28 }}
                  >
                    <MenuItem value="">-- Izaberi --</MenuItem>
                    {availableKeys.map(k => (
                      <MenuItem key={k} value={k}>{k}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.7rem' }}>Value polje</InputLabel>
                  <Select
                    value={currentOption.valueField || ''}
                    onChange={(e) => onFieldOptionChange(field.key, {
                      mode: 'keyValue',
                      valueField: e.target.value,
                    })}
                    label="Value polje"
                    sx={{ fontSize: '0.7rem', height: 28 }}
                  >
                    <MenuItem value="">-- Izaberi --</MenuItem>
                    {availableKeys.map(k => (
                      <MenuItem key={k} value={k}>{k}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                {/* Manual key/value input when no keys detected */}
                <TextField
                  size="small"
                  label="Key polje (ručno)"
                  value={currentOption.keyField || ''}
                  onChange={(e) => onFieldOptionChange(field.key, {
                    mode: 'keyValue',
                    keyField: e.target.value,
                  })}
                  placeholder="npr. AttributeCode"
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '0.7rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                  }}
                />
                <TextField
                  size="small"
                  label="Value polje (ručno)"
                  value={currentOption.valueField || ''}
                  onChange={(e) => onFieldOptionChange(field.key, {
                    mode: 'keyValue',
                    valueField: e.target.value,
                  })}
                  placeholder="npr. AttributeValue"
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '0.7rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                  }}
                />
              </>
            )}
          </Box>
        )}
      </TableCell>

      {/* Field Selector - Autocomplete with manual input */}
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Autocomplete
              freeSolo
              size="small"
              value={mapping || ''}
              onChange={(_event, newValue) => {
                onMappingChange(field.key, newValue || '');
              }}
              onInputChange={(_event, newInputValue, reason) => {
                // Only update on user input, not on selection (which triggers onChange)
                if (reason === 'input') {
                  onMappingChange(field.key, newInputValue);
                }
              }}
              options={availableFields.map(f => f.path)}
              getOptionLabel={(option) => {
                // Find display path for this option
                const fieldDef = availableFields.find(f => f.path === option);
                return fieldDef ? fieldDef.displayPath : option;
              }}
              renderOption={(props, option) => {
                const fieldDef = availableFields.find(f => f.path === option);
                return (
                  <li {...props} key={option}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {fieldDef ? fieldDef.displayPath : option}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Izaberi ili upiši XML path..."
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '0.85rem',
                    },
                  }}
                />
              )}
              sx={{ minWidth: 220, flex: 1 }}
            />
            {/* Transform toggle button */}
            {mapping && (
              <IconButton
                size="small"
                onClick={() => setShowTransform(!showTransform)}
                color={hasTransform ? 'primary' : 'default'}
                title="Transformacija vrijednosti"
                sx={{
                  bgcolor: hasTransform ? 'primary.dark' : 'transparent',
                  '&:hover': { bgcolor: hasTransform ? 'primary.main' : 'action.hover' },
                }}
              >
                <TransformIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Transform options */}
          {showTransform && mapping && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={currentOption.transform?.type || 'none'}
                  onChange={(e) => {
                    const type = e.target.value as any;
                    onFieldOptionChange(field.key, {
                      transform: type === 'none' ? undefined : { type, value: '', replaceWith: '' },
                    });
                  }}
                  sx={{ fontSize: '0.75rem', height: 28 }}
                >
                  <MenuItem value="none">Bez izmjene</MenuItem>
                  <MenuItem value="europeanNumber">EU broj (3,99 → 3.99)</MenuItem>
                  <MenuItem value="removePrefix">Ukloni prefix</MenuItem>
                  <MenuItem value="removeSuffix">Ukloni suffix</MenuItem>
                  <MenuItem value="replace">Zamijeni</MenuItem>
                  <MenuItem value="regex">Regex</MenuItem>
                  <MenuItem value="prepend">Dodaj prefix</MenuItem>
                  <MenuItem value="append">Dodaj suffix</MenuItem>
                  <MenuItem value="stripCss">Ukloni CSS (zadrži HTML)</MenuItem>
                  <MenuItem value="stripHtml">Ukloni sav HTML (čist tekst)</MenuItem>
                </Select>
              </FormControl>

              {currentOption.transform?.type && currentOption.transform.type !== 'none' && currentOption.transform.type !== 'europeanNumber' && currentOption.transform.type !== 'stripCss' && currentOption.transform.type !== 'stripHtml' && (
                <TextField
                  size="small"
                  value={currentOption.transform.value || ''}
                  onChange={(e) => onFieldOptionChange(field.key, {
                    transform: { ...currentOption.transform!, value: e.target.value },
                  })}
                  placeholder={
                    currentOption.transform.type === 'removePrefix' ? 'Prefix za ukloniti...' :
                    currentOption.transform.type === 'removeSuffix' ? 'Suffix za ukloniti...' :
                    currentOption.transform.type === 'prepend' ? 'Prefix za dodati...' :
                    currentOption.transform.type === 'append' ? 'Suffix za dodati...' :
                    'Vrijednost...'
                  }
                  sx={{
                    flex: 1,
                    minWidth: 100,
                    '& .MuiInputBase-input': { fontSize: '0.75rem' },
                  }}
                />
              )}

              {(currentOption.transform?.type === 'replace' || currentOption.transform?.type === 'regex') && (
                <TextField
                  size="small"
                  value={currentOption.transform.replaceWith || ''}
                  onChange={(e) => onFieldOptionChange(field.key, {
                    transform: { ...currentOption.transform!, replaceWith: e.target.value },
                  })}
                  placeholder="Zamijeni sa..."
                  sx={{
                    flex: 1,
                    minWidth: 80,
                    '& .MuiInputBase-input': { fontSize: '0.75rem' },
                  }}
                />
              )}
            </Box>
          )}

          {/* Price adjustment inputs - only for price fields */}
          {isPriceField && mapping && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <TextField
                size="small"
                type="number"
                label="Pomnoži sa"
                value={currentOption.priceMultiplier || ''}
                onChange={(e) => onFieldOptionChange(field.key, {
                  priceMultiplier: e.target.value ? parseFloat(e.target.value) : undefined,
                })}
                placeholder="1.955883"
                inputProps={{ step: 'any' }}
                sx={{
                  width: 130,
                  '& .MuiInputBase-input': { fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                }}
              />
              <TextField
                size="small"
                type="number"
                label="Dodaj %"
                value={currentOption.priceAddPercent || ''}
                onChange={(e) => onFieldOptionChange(field.key, {
                  priceAddPercent: e.target.value ? parseFloat(e.target.value) : undefined,
                })}
                placeholder="10"
                inputProps={{ step: 'any' }}
                sx={{
                  width: 100,
                  '& .MuiInputBase-input': { fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                }}
              />
            </Box>
          )}
        </Box>
      </TableCell>

      {/* Preview Value */}
      <TableCell sx={{ width: 200 }}>
        {mapping && previewValue !== undefined ? (
          Array.isArray(previewValue) ? (
            <Chip
              label={`[${previewValue.length} items]`}
              size="small"
              color="info"
              onClick={() => onPreviewArray?.(mapping)}
              icon={<PreviewIcon />}
              sx={{ cursor: 'pointer' }}
            />
          ) : (
            <Box>
              {/* Show original if transform applied */}
              {hasAnyTransform && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ wordBreak: 'break-all', textDecoration: 'line-through', display: 'block', fontSize: '0.65rem' }}
                >
                  {String(extractTextValue(previewValue) || '-').slice(0, 25)}
                </Typography>
              )}
              <Typography
                variant="caption"
                color={hasAnyTransform ? 'success.main' : 'text.secondary'}
                sx={{ wordBreak: 'break-all', fontWeight: hasAnyTransform ? 600 : 400 }}
              >
                {String(extractTextValue(transformedPreview) || '-').slice(0, 30)}
              </Typography>
            </Box>
          )
        ) : (
          <Typography variant="caption" color="text.secondary">-</Typography>
        )}
      </TableCell>
    </TableRow>
  );
}
